// The royal families, read off the snapshot (`globals.dynasty`, module
// `diplomacy.dynasty`). The client keeps no copy: a proposal that is answered,
// expires or is voided by a broken alliance simply leaves the snapshot.
import type { GameSnapshot } from '@aod/protocol';
import type { TreatyNews } from './diplomacy.js';

export type Whereabouts =
  | { readonly kind: 'home' }
  | { readonly kind: 'travelling'; readonly to: number }
  | { readonly kind: 'held'; readonly captor: number }
  | { readonly kind: 'dead' };

export interface Royal {
  readonly id: number;
  readonly seat: number;
  readonly female: boolean;
  /** The image id (`light-f-01`), or null when the pool ran dry. */
  readonly portrait: string | null;
  /** The royal this one is married to. */
  readonly spouseOf: number | null;
  readonly whereabouts: Whereabouts;
  /** When they were born, in ticks — present only while
   *  `diplomacy.generations` is armed (generations design §6). */
  readonly bornTick?: number;
  /** The derived life stage the sim publishes: child / young / middle /
   *  old. The client displays it, never computes it. Absent while the
   *  module is dark. */
  readonly stage?: LifeStage;
}

/** The four life stages (generations design §3). */
export type LifeStage = 'child' | 'young' | 'middle' | 'old';

export interface MarriageProposal {
  readonly from: number;
  readonly to: number;
  readonly expiresTick: number;
  /** The royals who would wed (the proposer's, then the target's), or null. */
  readonly royalFrom: number | null;
  readonly royalTo: number | null;
}

export interface Bond {
  readonly seatA: number;
  readonly seatB: number;
  readonly royalA: number;
  readonly royalB: number;
}

/** What a captor has decided to do with a royal they hold. */
export type CaptureDecision = 'ransom' | 'hold' | 'execute' | 'release';

/** A royal in captivity, and what their captor means to do with them. */
export interface Capture {
  readonly royal: number;
  /** The house (or band) holding them. */
  readonly captor: number;
  /** Where they are kept — a rescue goes there. */
  readonly at: { readonly q: number; readonly r: number };
  /** The tick the capture began. */
  readonly since: number;
  /** What the captor has decided, or null while they have not decided. */
  readonly choice: CaptureDecision | null;
  /** The ransom asked, in gold. */
  readonly price: number;
  /** The tick the ransom runs out. */
  readonly deadline: number;
  /** Set when a rescue has been tried and failed. */
  readonly alerted: boolean;
  /** How many rescue attempts have been made. */
  readonly rescues: number;
}

export interface Dynasty {
  readonly royals: readonly Royal[];
  readonly proposals: readonly MarriageProposal[];
  readonly bonds: readonly Bond[];
  readonly alliances: ReadonlyArray<readonly [number, number]>;
  /** The houses at a mutual non-aggression pact. A marriage may be
   *  offered across one (the sim upgrades the pact into an alliance
   *  when the marriage is accepted). */
  readonly pacts: ReadonlyArray<readonly [number, number]>;
  /** The royals in captivity. Empty while nobody has been taken. */
  readonly captures: readonly Capture[];
  /** Houses in mourning (generations PR C): the seat and the tick the
   *  mourning ends. Absent while `diplomacy.generations` is dark. */
  readonly mourning?: ReadonlyArray<{ readonly seat: number; readonly until: number }>;
}

/** The dynasty the sim published, or null while the module is off.
 *
 *  The wire is serde_wasm_bindgen, which serialises `Option::None` as
 *  `undefined` — not `null` as the types here say. Every "is this slot
 *  filled?" check in the client compares against `null` (and the sim's own
 *  JSON fixtures use `null`), so the read normalises the optional fields
 *  once, here, instead of at each call site. */
export function readDynasty(snapshot: GameSnapshot | null): Dynasty | null {
  const globals = snapshot?.state.globals as
    | {
        dynasty?: Omit<Dynasty, 'royals' | 'proposals' | 'captures' | 'pacts'> & {
          royals?: Royal[];
          proposals?: MarriageProposal[];
          captures?: Capture[];
          pacts?: ReadonlyArray<readonly [number, number]>;
        };
      }
    | undefined;
  const raw = globals?.dynasty;
  if (!raw) return null;
  return {
    ...raw,
    royals: (raw.royals ?? []).map((r) => ({
      ...r,
      portrait: r.portrait ?? null,
      spouseOf: r.spouseOf ?? null,
    })),
    proposals: (raw.proposals ?? []).map((p) => ({
      ...p,
      royalFrom: p.royalFrom ?? null,
      royalTo: p.royalTo ?? null,
    })),
    // A captor that has not decided yet says nothing: the wire sends the
    // absent choice as `undefined`, and every "has the captor decided?"
    // check compares against `null`.
    captures: (raw.captures ?? []).map((c) => ({ ...c, choice: c.choice ?? null })),
    // A snapshot from before the pact list existed carries no key at all.
    pacts: raw.pacts ?? [],
  };
}

/** A royal by id. */
export function royalById(dynasty: Dynasty, id: number | null): Royal | null {
  return id === null ? null : (dynasty.royals.find((r) => r.id === id) ?? null);
}

/** A house's royals, in id order. */
export function courtOf(dynasty: Dynasty, seat: number): readonly Royal[] {
  return dynasty.royals.filter((r) => r.seat === seat);
}

/** The royals a house holds, in id order. */
export function captivesOf(dynasty: Dynasty, captor: number): readonly Capture[] {
  return dynasty.captures.filter((c) => c.captor === captor);
}

/** The captivities that concern `seat`: the royals it holds, and the royals
 *  of its own house that someone else holds. */
export function capturesConcerning(
  dynasty: Dynasty,
  seat: number,
): {
  readonly held: readonly Capture[];
  readonly taken: readonly Capture[];
} {
  const mine = new Set(dynasty.royals.filter((r) => r.seat === seat).map((r) => r.id));
  return {
    held: dynasty.captures.filter((c) => c.captor === seat),
    taken: dynasty.captures.filter((c) => mine.has(c.royal)),
  };
}

/** The royal who would marry: unmarried and at court. */
export function freeRoyal(dynasty: Dynasty, seat: number): Royal | null {
  return (
    courtOf(dynasty, seat).find((r) => r.spouseOf === null && r.whereabouts.kind === 'home') ?? null
  );
}

/** May this house pay the ransom for this royal? The house the royal was born
 *  to, or the house it is marrying — the same rule the sim applies. */
export function mayPayRansom(dynasty: Dynasty, payer: number, royalId: number): boolean {
  const royal = royalById(dynasty, royalId);
  if (!royal) return false;
  if (royal.seat === payer) return true;
  return dynasty.bonds.some(
    (b) =>
      (b.royalA === royalId && b.seatB === payer) || (b.royalB === royalId && b.seatA === payer),
  );
}

/** May this house rescue this royal? The house the royal was born to, or the
 *  house it is marrying. */
export function mayRescue(dynasty: Dynasty, rescuer: number, royalId: number): boolean {
  const royal = royalById(dynasty, royalId);
  if (!royal) return false;
  if (royal.seat === rescuer) return true;
  return dynasty.bonds.some(
    (b) =>
      (b.royalA === royalId && b.seatB === rescuer) ||
      (b.royalB === royalId && b.seatA === rescuer),
  );
}

/** The marriage offered to `seat` by someone, if one is on the table. */
export function proposalTo(dynasty: Dynasty, seat: number): MarriageProposal | null {
  return dynasty.proposals.find((p) => p.to === seat) ?? null;
}

function samePair(a: number, b: number, x: number, y: number): boolean {
  return (a === x && b === y) || (a === y && b === x);
}

export type MarriageStanding = 'bonded' | 'pending' | 'incoming' | 'open' | 'pact' | 'not_allied';

/** Where a marriage between two houses stands, from `me`'s side. */
export function standingWith(dynasty: Dynasty, me: number, other: number): MarriageStanding {
  if (dynasty.bonds.some((b) => samePair(b.seatA, b.seatB, me, other))) return 'bonded';
  const offer = dynasty.proposals.find((p) => samePair(p.from, p.to, me, other));
  if (offer) return offer.from === me ? 'pending' : 'incoming';
  if (dynasty.alliances.some(([a, b]) => samePair(a, b, me, other))) return 'open';
  // A pact is enough to propose across: the sim upgrades it to an alliance
  // when the marriage is accepted.
  if (dynasty.pacts.some(([a, b]) => samePair(a, b, me, other))) return 'pact';
  return 'not_allied';
}

/** The image of a portrait id; the thumb is the 128px card version. A
 *  `stage` picks the age file (`<id>.<stage>.webp` — all 68 faces exist at
 *  all four stages, PR #433): the same face across a lifetime. */
export function portraitUrl(id: string, thumb = false, stage?: LifeStage): string {
  const suffix = `${stage ? `.${stage}` : ''}${thumb ? '--128' : ''}`;
  return `/assets/portraits/${id}${suffix}.webp`;
}

/** The houses currently in mourning, if the module publishes them. */
export function mourningHouses(
  dynasty: Dynasty,
): ReadonlyArray<{ readonly seat: number; readonly until: number }> {
  return dynasty.mourning ?? [];
}

/**
 * What became of the marriages you offered, between two snapshots: wed, refused
 * or lapsed. An offer leaving the table says nothing by itself, so a standing
 * bond and the clock tell the three endings apart.
 */
export function marriageNews(
  prev: Dynasty | null,
  next: Dynasty | null,
  me: number,
  tick: number,
): TreatyNews[] {
  if (!prev || !next) return [];
  const out: TreatyNews[] = [];
  for (const offer of prev.proposals) {
    if (offer.from !== me) continue;
    if (next.proposals.some((p) => p.from === offer.from && p.to === offer.to)) continue;
    const kind =
      standingWith(next, me, offer.to) === 'bonded'
        ? 'wed'
        : tick >= offer.expiresTick
          ? 'lapsed'
          : 'refused';
    out.push({ kind, other: offer.to, state: 'alliance' });
  }
  return out;
}
